import React, { useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "../../hooks/use-toast";
import AudioPlayer from "../../components/AudioPlayer";
import { X, Upload, Trash2, Eye, EyeOff } from "lucide-react";

const CHUNK = 5 * 1024 * 1024;
const MAX = 500 * 1024 * 1024;
const mb = (n) => `${(n / 1024 / 1024).toFixed(1)} MB`;

export const RecordingPanel = ({ event, onClose, onChanged }) => {
  const [rec, setRec] = useState(event.recording || null);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const base = `/admin/webinars/${event.id}/recording`;
  const err = (title, e) => toast({ title, description: e?.response?.data?.detail || "Try again." });

  const upload = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['mp3', 'm4a'].includes(ext)) return toast({ title: "Only MP3 or M4A files are supported" });
    if (file.size > MAX) return toast({ title: "File too large (max 500 MB)" });
    if (rec && !window.confirm(`Replace "${rec.filename}" with "${file.name}"?`)) return;
    const uploadId = `${Date.now()}${Math.random().toString(36).slice(2, 10)}`;
    const total = Math.ceil(file.size / CHUNK);
    setBusy(true); setProgress(0);
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append('upload_id', uploadId); fd.append('index', String(i));
        fd.append('chunk', file.slice(i * CHUNK, (i + 1) * CHUNK), `${i}`);
        let tries = 0;
        for (;;) {
          try { await adminApi.post(`${base}/chunk`, fd); break; }
          catch (e) { if (++tries >= 3) throw e; }
        }
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      const { data } = await adminApi.post(`${base}/complete`, { upload_id: uploadId, total_chunks: total, filename: file.name });
      setRec(data); onChanged(); toast({ title: "Recording uploaded", description: data.published ? "It is live for members." : "Publish it to make it visible to members." });
    } catch (e) { err("Upload failed", e); }
    finally { setBusy(false); setProgress(null); }
  };

  const togglePublish = async () => {
    setBusy(true);
    try { const { data } = await adminApi.patch(base, { published: !rec.published }); setRec(data); onChanged(); toast({ title: data.published ? "Recording published" : "Recording unpublished" }); }
    catch (e) { err("Update failed", e); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the recording "${rec.filename}"? Members will no longer be able to listen.`)) return;
    setBusy(true);
    try { await adminApi.delete(base); setRec(null); onChanged(); toast({ title: "Recording deleted" }); }
    catch (e) { err("Delete failed", e); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: 'rgba(13,42,32,0.55)' }} data-testid="recording-panel">
      <div className="w-full max-w-[560px] rounded-lg p-6" style={{ background: '#fbf8ef' }}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="hero-title text-[22px]">Recording</h3>
          <button onClick={onClose} disabled={busy && progress !== null} data-testid="recording-panel-close"><X size={18} /></button>
        </div>
        <p className="text-[13px] text-[#0d2a20]/70 mb-5">{event.title}</p>

        {rec ? (
          <div className="mb-5 space-y-3">
            <div className="flex items-center justify-between text-[13px]">
              <div><span className="font-medium" data-testid="recording-filename">{rec.filename}</span> <span className="text-[#0d2a20]/60">· {mb(rec.size)}</span></div>
              <span className="px-2 py-1 rounded text-[11px]" data-testid="recording-status" style={{ background: rec.published ? '#d6ecd6' : '#fde5c7' }}>{rec.published ? 'Published' : 'Unpublished'}</span>
            </div>
            <AudioPlayer key={rec.uploaded_at} testId="admin-recording-player" getUrl={async () => (await adminApi.get(`${base}/stream-url`)).data.url} />
            <div className="flex gap-2">
              <button onClick={togglePublish} disabled={busy} data-testid="recording-publish-toggle" className="inline-flex items-center gap-2 px-3 py-2 rounded-md btn-green text-[13px] disabled:opacity-60">
                {rec.published ? <><EyeOff size={14} /> Unpublish</> : <><Eye size={14} /> Publish to members</>}
              </button>
              <button onClick={remove} disabled={busy} data-testid="recording-delete" className="inline-flex items-center gap-2 px-3 py-2 rounded-md btn-ghost text-[13px] text-[#7a1f13] disabled:opacity-60"><Trash2 size={14} /> Delete</button>
            </div>
          </div>
        ) : <div className="mb-5 text-[13px] text-[#0d2a20]/60" data-testid="recording-none">No recording uploaded yet.</div>}

        <label className={`flex items-center justify-center gap-2 px-4 py-6 rounded-md border-2 border-dashed text-[13px] cursor-pointer transition-colors hover:bg-white ${busy ? 'opacity-60 pointer-events-none' : ''}`} style={{ borderColor: 'rgba(13,42,32,0.2)' }}>
          <Upload size={16} /> {rec ? 'Replace recording' : 'Upload recording'} (MP3 or M4A, max 500 MB)
          <input type="file" accept=".mp3,.m4a,audio/mpeg,audio/mp4,audio/x-m4a" className="hidden" data-testid="recording-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; upload(f); }} />
        </label>
        {progress !== null && (
          <div className="mt-3" data-testid="recording-upload-progress">
            <div className="h-2 rounded-full overflow-hidden" style={{ background: '#ece5d3' }}><div className="h-full transition-[width]" style={{ width: `${progress}%`, background: '#e0684d' }} /></div>
            <div className="text-[12px] text-[#0d2a20]/70 mt-1">Uploading… {progress}%</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordingPanel;
